import { useCallback, useEffect, useRef, useState } from "react";
import {
  eliminarDocumento,
  listarDocumentos,
  subirDocumento,
  verDocumentoUrl,
  descargarDocumentoUrl,
  actualizarEstadoDocumento,
  marcarNoAplicaDocumento,
} from "../../../../services/documentosService";
import { listarDocumentosAnalisis, retryAnalisisDocumento, obtenerAnalisisDocumento } from "../../../../services/documentoAnalisisService";
import { getSocket, initSocket } from "../../../../utils/socketClient";
import {
  DOCUMENTO_MAX_MB,
  DOCUMENTOS_MIME_PERMITIDOS,
  DOCUMENTOS_LABEL_FORMATOS,
  documentoLabel,
} from "../constants/documentos";
import { normalizeDocumento } from "../adapters/estudiantesAdapter";
import { showToast } from "../../../../utils/showToast";

export default function useDocumentosEstudiante(estudianteId) {
  const [documentos, setDocumentos] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [retryingAnalisis, setRetryingAnalisis] = useState(false);

  const cargarDocumentos = useCallback(async () => {
    if (!estudianteId) {
      setDocumentos([]);
      return;
    }
    setLoadingDocs(true);
    try {
      const [docsRes, analisisRes] = await Promise.allSettled([
        listarDocumentos(estudianteId),
        listarDocumentosAnalisis({ estudiante_id: estudianteId, page_size: 100 }),
      ]);

      const parsed = Array.isArray(docsRes.value?.data)
        ? docsRes.value.data
        : docsRes.value || [];

      // Construir mapa de analisis por documento_id
      const analisisItems = analisisRes.value?.items || analisisRes.value?.data || [];
      const analisisMap = {};
      analisisItems.forEach((item) => {
        analisisMap[item.id] = item;
      });

      setDocumentos(
        parsed.map((doc) => {
          const analisis = analisisMap[doc.id] || {};
          return normalizeDocumento({ ...doc, ...analisis });
        })
      );
      setDocsError(null);
    } catch (err) {
      console.error(err);
      setDocsError(err.message || "No se pudieron cargar documentos.");
      showToast({ type: "danger", message: err.message || "No se pudieron cargar documentos." });
    } finally {
      setLoadingDocs(false);
    }
  }, [estudianteId]);

  useEffect(() => {
    cargarDocumentos();
  }, [cargarDocumentos]);

  // Actualización en tiempo real cuando el OCR termina
  useEffect(() => {
    if (!estudianteId) return;
    const token = localStorage.getItem('token');
    const socket = getSocket() || initSocket(token);
    if (!socket) return;

    const aplicarResultado = (data) => {
      if (!data?.documento_id) return;
      setDocumentos((prev) =>
        prev.map((doc) => {
          if (doc.id !== data.documento_id) return doc;
          return {
            ...doc,
            decisionAnalisis: data.decision_automatica ?? doc.decisionAnalisis,
            analisisRiesgo:   data.nivel_riesgo        ?? doc.analisisRiesgo,
            analisisScore:    data.score_confianza      ?? doc.analisisScore,
            analisisEstado:   data.analisis_estado      ?? doc.analisisEstado,
            ...(data.decision_automatica === 'aprobado'  && { estado: 'aprobado'  }),
            ...(data.decision_automatica === 'rechazado' && { estado: 'rechazado' }),
            ...(data.resultado_antecedentes != null && { resultadoAntecedentes: data.resultado_antecedentes }),
          };
        })
      );
    };

    socket.on('documento:analizado', aplicarResultado);
    return () => socket.off('documento:analizado', aplicarResultado);
  }, [estudianteId]);

  // Polling de respaldo: refresca OCR de docs pendientes cada 6s hasta que todos tengan resultado
  // "pendiente" y null/undefined se consideran sin resultado final
  const esPendiente = (d) => !d.decisionAnalisis || d.decisionAnalisis === 'pendiente';

  const pollingRef = useRef(null);
  // Guarda documentos en ref para leerlos dentro del interval sin re-crear el interval
  const documentosRef = useRef(documentos);
  useEffect(() => { documentosRef.current = documentos; }, [documentos]);

  useEffect(() => {
    if (!estudianteId) return;
    const limpiar = () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };

    pollingRef.current = setInterval(async () => {
      // Verificar si hay docs pendientes usando la ref (sin depender del estado)
      const hayPendientes = documentosRef.current.some((d) => d.id && esPendiente(d));
      if (!hayPendientes) { limpiar(); return; }

      try {
        const res = await listarDocumentosAnalisis({ estudiante_id: estudianteId, page_size: 100 });
        const items = res?.items || [];
        if (!items.length) return;

        const analisisMap = {};
        items.forEach((item) => { analisisMap[item.id] = item; });

        let alguienResolvio = false;
        setDocumentos((prev) => {
          const actualizados = prev.map((doc) => {
            const a = analisisMap[doc.id];
            if (!a) return doc;
            const nuevaDecision = a.analisis_decision_actual ?? doc.decisionAnalisis;
            if (esPendiente(doc) && nuevaDecision && !esPendiente({ decisionAnalisis: nuevaDecision })) {
              alguienResolvio = true;
            }
            return {
              ...doc,
              decisionAnalisis: nuevaDecision,
              analisisRiesgo:   a.analisis_riesgo_actual   ?? doc.analisisRiesgo,
              analisisEstado:   a.analisis_estado_actual   ?? doc.analisisEstado,
              analisisScore:    a.analisis_score_actual    ?? doc.analisisScore,
            };
          });
          return actualizados;
        });
        // Si algún doc resolvió OCR, recargar lista completa para obtener resultado_antecedentes y estado actualizados
        if (alguienResolvio) cargarDocumentos();
      } catch (_) { /* silencioso */ }
    }, 4000);

    return limpiar;
  }, [estudianteId, cargarDocumentos]);

  const validarArchivo = (file) => {
    if (!file) return "Selecciona un archivo.";
    if (!DOCUMENTOS_MIME_PERMITIDOS.includes(file.type)) {
      return `Tipo de archivo no permitido (solo ${DOCUMENTOS_LABEL_FORMATOS}).`;
    }
    if (file.size > DOCUMENTO_MAX_MB * 1024 * 1024) {
      return `El archivo supera ${DOCUMENTO_MAX_MB}MB.`;
    }
    return null;
  };

  const handleSubir = useCallback(
    async ({ file, tipo_documento, descripcion }) => {
      if (!estudianteId) {
        showToast({ type: "warning", message: "Selecciona un estudiante." });
        return false;
      }
      const validation = validarArchivo(file);
      if (validation) {
        showToast({ type: "warning", message: validation });
        return false;
      }
      setUploading(true);
      try {
        await subirDocumento({ file, estudiante_id: estudianteId, tipo_documento, descripcion });
        showToast({
          type: "success",
          message: `Documento ${documentoLabel(tipo_documento)} subido.`,
        });
        await cargarDocumentos();
        return true;
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo subir el documento." });
        return false;
      } finally {
        setUploading(false);
      }
    },
    [estudianteId, cargarDocumentos]
  );

  const handleEliminarDoc = useCallback(
    async (id) => {
      if (!id) return;
      try {
        await eliminarDocumento(id);
        setDocumentos((prev) => prev.filter((doc) => doc.id !== id));
        showToast({ type: "success", message: "Documento eliminado." });
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo eliminar el documento." });
      }
    },
    []
  );

  const getVerUrl = useCallback((id) => verDocumentoUrl(id), []);
  const getDescargarUrl = useCallback((id) => descargarDocumentoUrl(id), []);

  const handleCambiarEstado = useCallback(
    async (id, estado, extra = {}) => {
      if (!id) return;
      const payload = {};
      if (estado !== undefined && estado !== null && estado !== "") payload.estado = estado;
      if (extra && typeof extra === "object") Object.assign(payload, extra);
      if (!Object.keys(payload).length) return;
      try {
        await actualizarEstadoDocumento(id, payload);
        setDocumentos((prev) =>
          prev.map((doc) => {
            if (doc.id !== id) return doc;
            const next = { ...doc };
            if (payload.estado !== undefined) next.estado = payload.estado;
            if (payload.resultado_antecedentes !== undefined) {
              next.resultadoAntecedentes = payload.resultado_antecedentes || null;
            }
            return next;
          })
        );
        showToast({ type: "success", message: "Estado actualizado." });
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo actualizar el estado." });
      }
    },
    []
  );

  const handleMarcarNoAplica = useCallback(
    async ({ tipo_documento }) => {
      if (!estudianteId || !tipo_documento) return false;
      try {
        await marcarNoAplicaDocumento({ estudiante_id: estudianteId, tipo_documento });
        await cargarDocumentos();
        showToast({ type: "success", message: "Documento marcado como no aplica." });
        return true;
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo marcar como no aplica." });
        return false;
      }
    },
    [estudianteId, cargarDocumentos]
  );

  const handleVerAnalisis = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await obtenerAnalisisDocumento(id);
      return res || null;
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: "No se pudo cargar el detalle del análisis." });
      return null;
    }
  }, []);

  const handleRetryAnalisis = useCallback(
    async (id) => {
      if (!id || retryingAnalisis) return;
      setRetryingAnalisis(true);
      try {
        await retryAnalisisDocumento(id);
        showToast({ type: "success", message: "Documento reencolado para análisis OCR." });
        // Actualizar estado local optimista a "pendiente"
        setDocumentos((prev) =>
          prev.map((doc) =>
            doc.id === id
              ? { ...doc, decisionAnalisis: "pendiente", analisisRiesgo: null, analisisEstado: "pendiente" }
              : doc
          )
        );
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo reencolar el análisis." });
      } finally {
        setRetryingAnalisis(false);
      }
    },
    [retryingAnalisis]
  );

  return {
    documentos,
    loadingDocs,
    docsError,
    uploading,
    retryingAnalisis,
    cargarDocumentos,
    handleSubir,
    handleEliminarDoc,
    getVerUrl,
    getDescargarUrl,
    handleCambiarEstado,
    handleMarcarNoAplica,
    handleRetryAnalisis,
    handleVerAnalisis,
  };
}
